<script setup lang="ts">
const { data: page } = await useAsyncData('ueberuns', () => queryCollection('ueberuns').first())
const { data: schulungsraum } = await useAsyncData('ueberuns-schulungsraum', () => queryCollection('ueberuns_schulungsraum').first())

const title = page.value?.seo?.title || page.value?.title
const description = page.value?.seo?.description || page.value?.description

useSeoMeta({
  titleTemplate: '',
  title,
  ogTitle: title,
  description,
  ogDescription: description
})
</script>

<template>
  <div v-if="page">
    <UPageHero
      :description="page.description"
      align="center"
    >
      <template #top>
        <HeroBackground />
      </template>

      <template #title>
        <MDC
          :value="page.title"
          unwrap="p"
        />
      </template>
    </UPageHero>

    <UPageSection>
      <UPageGrid>
        <UPageCard
          v-for="(card, index) in page.cards"
          :key="index"
          :title="card.title"
          :description="card.description"
          :icon="card.icon"
          :to="card.to"
          :ui="{ container: 'flex flex-col justify-between h-full' }"
          spotlight
        >
          <template #footer>
            <UButton
              :label="card.label ?? ''"
              :to="card.to"
              trailing-icon="i-lucide-arrow-right"
              variant="subtle"
              size="sm"
            />
          </template>
        </UPageCard>
      </UPageGrid>
    </UPageSection>

    <USeparator />

    <!-- Schulungsraum Hero -->
    <UPageHero
      v-if="schulungsraum"
      :description="schulungsraum.description"
      align="left"
    >
      <template #title>
        <MDC
          :value="schulungsraum.title"
          unwrap="p"
        />
      </template>
    </UPageHero>

    <!-- Schulungsraum & FooBar Sections -->
    <template v-if="schulungsraum">
      <UPageSection
        v-for="(section, index) in schulungsraum.sections"
        :key="index"
        :title="section.title"
        :description="section.description"
        :orientation="section.orientation"
        :reverse="section.reverse ?? false"
      >
        <template #features>
          <UPageFeature
            v-for="(feature, fIndex) in section.features"
            :key="fIndex"
            :title="feature.name ?? ''"
            :description="feature.description"
            :icon="feature.icon"
          />
        </template>

        <NuxtImg
          v-if="section.image?.src"
          :src="section.image.src"
          :alt="section.image.alt || section.title"
          class="w-full rounded-lg shadow-xl"
        />
      </UPageSection>

      <!-- FooBar Formate: direkt unter "Willkommen in der FooBar!" -->
      <UPageSection
        v-if="schulungsraum.foobarFormats?.length"
        id="foobar"
      >
        <UContainer>
          <div class="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto">
            <UCard
              v-for="(format, index) in schulungsraum.foobarFormats"
              :key="index"
            >
              <template #header>
                <div class="flex items-center gap-3">
                  <UIcon
                    :name="format.icon"
                    class="text-primary text-xl shrink-0"
                  />
                  <h3 class="font-semibold">
                    {{ format.title }}
                  </h3>
                </div>
              </template>
              <p class="text-sm text-muted">
                {{ format.description }}
              </p>
              <template #footer>
                <UButton
                  :to="format.linkTo"
                  :label="format.linkLabel"
                  variant="subtle"
                  trailing-icon="i-lucide-arrow-right"
                  size="sm"
                />
              </template>
            </UCard>
          </div>
        </UContainer>
      </UPageSection>

      <UPageSection
        v-if="schulungsraum.foobarFormats?.length"
        title="Nächste FooBar Termine"
      >
        <UContainer class="max-w-4xl">
          <UpcomingList
            :limit="10"
            category="Allgemein"
          />
        </UContainer>
      </UPageSection>

      <UPageSection v-if="schulungsraum.facts?.length">
        <UPageGrid>
          <UPageCard
            v-for="(fact, index) in schulungsraum.facts"
            :key="index"
            :title="fact.label"
            :description="fact.value"
            :icon="fact.icon"
            variant="subtle"
          />
        </UPageGrid>
      </UPageSection>
    </template>

    <USeparator />

    <UPageCTA
      v-if="page.cta"
      v-bind="page.cta"
      variant="naked"
      class="overflow-hidden"
    >
      <LazyStarsBg />
    </UPageCTA>
  </div>
</template>
